import { readAnalyticsConsent } from './consent';
import { getAttributionProperties } from './attribution';
import { trackMetaStandardEvent } from './metaPixelEvents';
import {
  createMetaEventId,
  isClientCapiEvent,
  mapPostHogEventToMeta,
  type MetaStandardEventName,
} from './metaStandardEvents';

export interface MetaFunnelUser {
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  country?: string;
  userId?: string;
}

export interface TrackMetaFunnelParams {
  eventName: MetaStandardEventName;
  eventId?: string;
  transactionId?: string;
  value?: number;
  currency?: string;
  contentName?: string;
  contentIds?: string[];
  user?: MetaFunnelUser;
  extra?: Record<string, unknown>;
}

/**
 * Pixel custom_data. Never put user fields here: fbq sends custom_data
 * unhashed in the request URL, and Meta flags that as prohibited PII.
 * User fields go only to /api/analytics/meta-event, which hashes them.
 */
export function buildMetaCustomData(params: TrackMetaFunnelParams): Record<string, unknown> {
  const data: Record<string, unknown> = { ...params.extra };
  if (typeof params.value === 'number' && Number.isFinite(params.value)) {
    data.value = Number(params.value.toFixed(2));
  }
  if (params.currency) data.currency = params.currency.toUpperCase();
  if (params.contentName) data.content_name = params.contentName;
  if (params.contentIds?.length) data.content_ids = params.contentIds;
  return data;
}

/**
 * Run client analytics after the current user-flow work.
 * Never throws. Never awaited. Must not change navigation or UI.
 */
export function queueSafeAnalytics(work: () => void): void {
  const run = () => {
    try {
      work();
    } catch {
      // Tracking must not change navigation, UI, or error handling.
    }
  };
  try {
    if (typeof queueMicrotask === 'function') {
      queueMicrotask(run);
      return;
    }
  } catch {
    // Fall through to the same-tick try/catch.
  }
  run();
}

export function trackMetaFunnelEvent(params: TrackMetaFunnelParams): string | null {
  try {
    if (typeof window === 'undefined') return null;
    if (readAnalyticsConsent() !== 'accepted') return null;

    const eventId = createMetaEventId({
      eventName: params.eventName,
      transactionId: params.transactionId,
      randomId: params.eventId,
    });
    const data = buildMetaCustomData(params);

    trackMetaStandardEvent({ eventName: params.eventName, eventId, data });

    if (isClientCapiEvent(params.eventName)) {
      const body = {
        eventName: params.eventName,
        eventId,
        customData: data,
        user: params.user,
        eventSourceUrl: window.location.href,
        attribution: getAttributionProperties(),
      };
      try {
        void fetch('/api/analytics/meta-event', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          keepalive: true,
        }).catch(() => {
          // CAPI is best-effort; Pixel already fired.
        });
      } catch {
        // fetch / JSON.stringify / TextEncoder must not throw into a submit handler.
      }
    }

    return eventId;
  } catch {
    return null;
  }
}

const purchaseAttempts = new Set<string>();

/**
 * Purchase after the payment success redirect. The server checks the
 * transaction against `payment_callbacks` and sends CAPI with the charged
 * amount; the Pixel then fires with the same event_id (transactionId).
 * `pixelAlreadyFired` is true when the redirect carried `meta_purchase=1`,
 * because AnalyticsProvider's trackMetaPurchase has already sent the Pixel.
 */
export async function sendVerifiedMetaPurchase(params: {
  transactionId: string;
  pixelAlreadyFired: boolean;
}): Promise<void> {
  try {
    if (typeof window === 'undefined') return;
    if (readAnalyticsConsent() !== 'accepted') return;
    if (purchaseAttempts.has(params.transactionId)) return;
    purchaseAttempts.add(params.transactionId);
  } catch {
    return;
  }

  try {
    const response = await fetch('/api/analytics/meta-purchase', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ transactionId: params.transactionId }),
    });
    if (!response.ok) {
      // 5xx / rate limit: allow a retry (e.g. on the consent event). 4xx is final.
      if (response.status >= 500 || response.status === 429) {
        purchaseAttempts.delete(params.transactionId);
      }
      return;
    }
    const json = (await response.json()) as {
      purchase?: { value: number; currency: string; planId?: string };
    };
    if (params.pixelAlreadyFired || !json.purchase) return;

    trackMetaStandardEvent({
      eventName: 'Purchase',
      eventId: params.transactionId,
      data: {
        value: json.purchase.value,
        currency: json.purchase.currency,
        ...(json.purchase.planId ? { content_ids: [json.purchase.planId] } : {}),
        content_type: 'product',
      },
    });
  } catch {
    // Network error: allow a retry. Best-effort; never affects the success page.
    purchaseAttempts.delete(params.transactionId);
  }
}

/** Fire the mapped Meta event when a PostHog funnel event is captured. */
export function trackMetaFromPostHogEvent(
  postHogEvent: string,
  params?: Record<string, unknown>
): string | null {
  try {
    const mapped = mapPostHogEventToMeta(postHogEvent);
    // Purchase CAPI also fires from the success-page redirect params (not here).
    // Lead with email is sent from the existing popup/waitlist submit handlers.
    if (mapped !== 'ViewContent' && mapped !== 'InitiateCheckout' && mapped !== 'CompleteRegistration') {
      return null;
    }

    const transactionId =
      typeof params?.transaction_id === 'string' ? params.transaction_id : undefined;
    const value =
      typeof params?.amount === 'number'
        ? params.amount
        : typeof params?.final_price === 'number'
          ? params.final_price
          : typeof params?.value === 'number'
            ? params.value
            : undefined;
    const currency = typeof params?.currency === 'string' ? params.currency : undefined;
    const contentName =
      (typeof params?.plan_selected === 'string' && params.plan_selected) ||
      (typeof params?.plan_purchased === 'string' && params.plan_purchased) ||
      (typeof params?.content_name === 'string' && params.content_name) ||
      undefined;
    const planId =
      (typeof params?.subscription_plan_id === 'string' && params.subscription_plan_id) ||
      (typeof params?.plan_id === 'string' && params.plan_id) ||
      undefined;

    return trackMetaFunnelEvent({
      eventName: mapped,
      transactionId,
      value,
      currency,
      contentName,
      contentIds: planId ? [planId] : undefined,
    });
  } catch {
    return null;
  }
}
